import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { client } from './apollo';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { FeatureProvider } from './context/FeatureContext';
import Layout from './components/layout/Layout';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminPage from './pages/AdminPage';
import DashboardPage from './pages/DashboardPage';
import AttendancePage from './pages/AttendancePage';
import CgpaPage from './pages/CgpaPage';
import ExamsPage from './pages/ExamsPage';
import NotesPage from './pages/NotesPage';
import FlashcardsPage from './pages/FlashcardsPage';
import ProjectsPage from './pages/ProjectsPage';
import ExpensesPage from './pages/ExpensesPage';
import MarketplacePage from './pages/MarketplacePage';
import TimetablePage from './pages/TimetablePage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import AcademicHealthPage from './pages/AcademicHealthPage';
import KnowledgePage from './pages/KnowledgePage';
import GrievancePage from './pages/GrievancePage';
import SyllabusPage from './pages/SyllabusPage';
import ResumePage from './pages/ResumePage';
import LinkedInPage from './pages/LinkedInPage';
import DocumentVaultPage from './pages/DocumentVaultPage';
import SportsPage from './pages/SportsPage';
import ShowcasePage from './pages/ShowcasePage';
import HackathonPage from './pages/HackathonPage';
import InitiativesPage from './pages/InitiativesPage';
import NetworkPage from './pages/NetworkPage';
import TechSocialPage from './pages/TechSocialPage';
import OpportunitiesPage from './pages/OpportunitiesPage';
import CollegeAdminPage from './pages/CollegeAdminPage';
import CampusNewsPage from './pages/CampusNewsPage';
import ScholarshipPage from './pages/ScholarshipPage';
import MaterialsPage from './pages/MaterialsPage';
import PlaylistsPage from './pages/PlaylistsPage';
import ForumsPage from './pages/ForumsPage';
import PgFinderPage from './pages/PgFinderPage';
import FoodSharePage from './pages/FoodSharePage';
import CabSharePage from './pages/CabSharePage';
import MarketingPage from './pages/MarketingPage';
import YearTargetPage from './pages/YearTargetPage';

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" replace />;
}

function AppShell() {
  const { user } = useAuth();
  return user ? <Layout /> : <MarketingPage />;
}

export default function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
    <ApolloProvider client={client}>
      <FeatureProvider>
      <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/" element={<AppShell />}>
              <Route index element={<DashboardPage />} />
              <Route path="attendance" element={<AttendancePage />} />
              <Route path="cgpa" element={<CgpaPage />} />
              <Route path="exams" element={<ExamsPage />} />
              <Route path="notes" element={<NotesPage />} />
              <Route path="flashcards" element={<FlashcardsPage />} />
              <Route path="projects" element={<ProjectsPage />} />
              <Route path="expenses" element={<ExpensesPage />} />
              <Route path="marketplace" element={<MarketplacePage />} />
              <Route path="timetable" element={<TimetablePage />} />
              <Route path="announcements" element={<AnnouncementsPage />} />
              <Route path="health" element={<AcademicHealthPage />} />
              <Route path="knowledge" element={<KnowledgePage />} />
              <Route path="grievances" element={<GrievancePage />} />
              <Route path="syllabus" element={<SyllabusPage />} />
              <Route path="resume" element={<ResumePage />} />
              <Route path="linkedin" element={<LinkedInPage />} />
              <Route path="documents" element={<DocumentVaultPage />} />
              <Route path="sports" element={<SportsPage />} />
              <Route path="showcase" element={<ShowcasePage />} />
              <Route path="hackathons" element={<HackathonPage />} />
              <Route path="initiatives" element={<InitiativesPage />} />
              <Route path="network" element={<NetworkPage />} />
              <Route path="social" element={<TechSocialPage />} />
              <Route path="opportunities" element={<OpportunitiesPage />} />
              <Route path="college-admin" element={<CollegeAdminPage />} />
              <Route path="campus-news" element={<CampusNewsPage />} />
              <Route path="scholarships" element={<ScholarshipPage />} />
              <Route path="materials" element={<MaterialsPage />} />
              <Route path="playlists" element={<PlaylistsPage />} />
              <Route path="forums" element={<ForumsPage />} />
              <Route path="pg-finder" element={<PgFinderPage />} />
              <Route path="food" element={<FoodSharePage />} />
              <Route path="cab" element={<CabSharePage />} />
              <Route path="feed" element={<Navigate to="/social" replace />} />
              <Route path="exchange" element={<Navigate to="/marketplace" replace />} />
              <Route path="study-groups" element={<Navigate to="/network" replace />} />
              <Route path="year-target" element={<YearTargetPage />} />
              <Route path="settings"   element={<SettingsPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
      </FeatureProvider>
    </ApolloProvider>
    </GoogleOAuthProvider>
  );
}
